import { tempConfigData } from "../../app/state/state.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { fetchSharedTeams, renderTeamTriggers } from "./teamSettings.js";
import {
    getEventLabel,
    getNotificationConfigWithRequiredEvents,
} from "./personalNotificationSettings.js";
import {
    getNotificationGroupOptions,
    getNotificationLayoutConfig,
    getNotificationSettingsGroups,
    NOTIFICATION_SETTINGS_SURFACES,
} from "../../app/notifications/notificationSettingsModel.js";
import {
    escapeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    INLINE_EDIT_INPUT_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import {
    bindDrugNotificationTargetSettings,
    renderDrugNotificationTargetSettingsSection,
} from "./drugNotificationTargetSettings.js";

let updateSaveButtonHandler = () => {};
const EVENT_ORDER_STEP = 10;
const ADMIN_CONTROL_MODE_OPTIONS = Object.freeze([
    { value: "user_control", label: "User" },
    { value: "locked_on", label: "Lock On" },
    { value: "locked_off", label: "Lock Off" },
]);
const ADMIN_TIME_CONTROL_MODE_OPTIONS = Object.freeze([
    { value: "user_control", label: "User" },
    { value: "locked_on", label: "Lock" },
]);

function escapeAttribute(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Registers the save-button dirty state callback for admin notification settings.
 *
 * @param {Function} handler Dirty-state callback.
 * @returns {void}
 */
export function setAdminNotificationSettingsUpdateSaveButton(handler) {
    updateSaveButtonHandler = handler || (() => {});
}

/**
 * Applies admin notification control/default changes into temp config state.
 *
 * @param {Event} e Change event.
 * @returns {void}
 */
export function handleAdminNotificationChange(e) {
    const input = e.target;
    const key = input.dataset.key;
    const targetType = input.dataset.target;
    const field = input.dataset.field;
    const inputType = input.dataset.type;

    if (!tempConfigData.adminNotificationControls) {
        tempConfigData.adminNotificationControls = {};
    }
    if (!tempConfigData.adminNotificationDefaults) {
        tempConfigData.adminNotificationDefaults = {};
    }

    let value;
    if (inputType === "visibility") {
        value = input.checked ? "visible" : "hidden";
    } else if (inputType === "bool") {
        value = input.checked;
    } else if (input.type === "number") {
        value = parseInt(input.value, 10) || 0;
    } else {
        value = input.value;
    }

    if (targetType === "control") {
        if (!tempConfigData.adminNotificationControls[key]) {
            tempConfigData.adminNotificationControls[key] = {};
        }
        tempConfigData.adminNotificationControls[key][field] = value;
    } else if (targetType === "default") {
        if (!tempConfigData.adminNotificationDefaults[key]) {
            tempConfigData.adminNotificationDefaults[key] = {};
        }
        tempConfigData.adminNotificationDefaults[key][field] = value;
    }

    updateSaveButtonHandler(true);
}

function handleNotificationLayoutChange(e, container) {
    const input = e.target;
    const groupId = input.dataset.groupId;
    const eventId = input.dataset.eventId;
    const field = input.dataset.field;
    const { notificationGroups, notificationEventLayout } =
        getNotificationLayoutConfig(tempConfigData, true);

    let value = input.type === "number"
        ? parseInt(input.value, 10) || 0
        : input.value;

    if (groupId) {
        if (!notificationGroups[groupId]) {
            notificationGroups[groupId] = {};
        }
        notificationGroups[groupId][field] = value;
    } else if (eventId) {
        if (!notificationEventLayout[eventId]) {
            notificationEventLayout[eventId] = {};
        }
        if (field === "order") {
            value = parseInt(input.value, 10) || 0;
        }
        notificationEventLayout[eventId][field] = value;
    }

    updateSaveButtonHandler(true);
    renderAdminNotificationSettingsTab(container);
}

function setAdminNotificationField({
    key,
    targetType,
    field,
    value,
}) {
    if (!tempConfigData.adminNotificationControls) {
        tempConfigData.adminNotificationControls = {};
    }
    if (!tempConfigData.adminNotificationDefaults) {
        tempConfigData.adminNotificationDefaults = {};
    }

    if (targetType === "control") {
        if (!tempConfigData.adminNotificationControls[key]) {
            tempConfigData.adminNotificationControls[key] = {};
        }
        tempConfigData.adminNotificationControls[key][field] = value;
    } else if (targetType === "default") {
        if (!tempConfigData.adminNotificationDefaults[key]) {
            tempConfigData.adminNotificationDefaults[key] = {};
        }
        tempConfigData.adminNotificationDefaults[key][field] = value;
    }

    updateSaveButtonHandler(true);
}

function getAdminNotificationSettingsGroups(controls) {
    return getNotificationSettingsGroups(tempConfigData, {
        controls,
        surface: NOTIFICATION_SETTINGS_SURFACES.STANDARD,
    });
}

function setNotificationEventLayout(eventId, updates) {
    const { notificationEventLayout } =
        getNotificationLayoutConfig(tempConfigData, true);

    if (!notificationEventLayout[eventId]) {
        notificationEventLayout[eventId] = {};
    }

    notificationEventLayout[eventId] = {
        ...notificationEventLayout[eventId],
        ...updates,
    };
}

function persistGroupEventOrder(group) {
    group.events.forEach((event, index) => {
        setNotificationEventLayout(event.id, {
            groupId: group.id,
            order: (index + 1) * EVENT_ORDER_STEP,
        });
    });
}

function moveNotificationEventWithinGroup(eventId, direction, controls, container) {
    const groups = getAdminNotificationSettingsGroups(controls);
    const group = groups.find((candidate) =>
        candidate.events.some((event) => event.id === eventId)
    );
    const currentIndex = group?.events.findIndex((event) => event.id === eventId) ?? -1;
    const nextIndex = currentIndex + direction;

    if (!group || currentIndex < 0 || nextIndex < 0 || nextIndex >= group.events.length) {
        return;
    }

    const nextEvents = [...group.events];
    [nextEvents[currentIndex], nextEvents[nextIndex]] = [
        nextEvents[nextIndex],
        nextEvents[currentIndex],
    ];

    persistGroupEventOrder({
        ...group,
        events: nextEvents,
    });
    updateSaveButtonHandler(true);
    renderAdminNotificationSettingsTab(container);
}

function getTargetGroupAppendOrder(groupId, eventId, controls) {
    const groups = getAdminNotificationSettingsGroups(controls);
    const group = groups.find((candidate) => candidate.id === groupId);
    const otherOrders = (group?.events || [])
        .filter((event) => event.id !== eventId)
        .map((event) => Number(event.order))
        .filter((order) => Number.isFinite(order));
    const maxOrder = otherOrders.length > 0 ? Math.max(...otherOrders) : 0;

    return maxOrder + EVENT_ORDER_STEP;
}

function openNotificationGroupMoveModal(event, controls, container) {
    const groupOptions = getNotificationGroupOptions(tempConfigData);
    const eventName = escapeHTML(event.label || getEventLabel(event.id));
    const currentGroupId = event.definition?.groupId || "";
    const currentLayout = tempConfigData.notificationEventLayout?.[event.id] || {};
    const selectedGroupId = currentLayout.groupId || currentGroupId;
    let selectedTargetGroupId = selectedGroupId;

    openConfirmationModal(
        "Change notification group",
        `
            <div class="space-y-4">
                <p>
                    Select which group you would like to move
                    <strong>${eventName}</strong> to.
                </p>
                <div id="notification-group-move-select-control"></div>
            </div>
        `,
        () => {
            const nextGroupId = selectedTargetGroupId || selectedGroupId;
            if (!nextGroupId || nextGroupId === selectedGroupId) {return;}

            setNotificationEventLayout(event.id, {
                groupId: nextGroupId,
                order: getTargetGroupAppendOrder(nextGroupId, event.id, controls),
            });
            updateSaveButtonHandler(true);
            renderAdminNotificationSettingsTab(container);
        },
        false,
        "Move",
    );

    const selectRoot = document.getElementById("notification-group-move-select-control");
    if (!selectRoot) {return;}

    createSelectControl({
        root: selectRoot,
        options: groupOptions.map((option) => ({
            value: option.id,
            label: option.label,
            searchText: `${option.label || ""} ${option.id || ""}`,
        })),
        value: selectedGroupId,
        searchable: groupOptions.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        placeholder: "Select group",
        accessibleName: "Notification group",
        mirror: false,
        onChange: (value) => {
            selectedTargetGroupId = value || selectedGroupId;
        },
    });
}

function renderAdminNotificationToggle(isChecked, dataset) {
    return `
        <label class="toggle-switch admin-notification-toggle-switch">
            <input type="checkbox" class="admin-notif-toggle" ${isChecked ? "checked" : ""} ${dataset}>
            <span class="slider"></span>
        </label>
    `;
}

function mountAdminNotificationSelectControls(container) {
    container.querySelectorAll("[data-admin-notif-select-control]").forEach((root) => {
        const key = root.dataset.key;
        const targetType = root.dataset.target;
        const field = root.dataset.field;
        const currentValue = root.dataset.value || "";
        const options = field === "days" ?
            ADMIN_TIME_CONTROL_MODE_OPTIONS :
            ADMIN_CONTROL_MODE_OPTIONS;

        createSelectControl({
            root,
            options,
            value: currentValue,
            searchable: false,
            portalMenu: true,
            placeholder: "Select mode",
            accessibleName: root.dataset.label || "Notification control mode",
            mirror: false,
            onChange: (value) => {
                setAdminNotificationField({
                    key,
                    targetType,
                    field,
                    value,
                });
            },
        });
    });
}

/**
 * Renders the admin-only Notifications tab in Settings.
 *
 * @param {HTMLElement} container The tab container element.
 * @returns {void}
 */
export function renderAdminNotificationSettingsTab(container) {
    const { controls, defaults } = getNotificationConfigWithRequiredEvents(
        tempConfigData,
        true,
    );
    getNotificationLayoutConfig(tempConfigData, true);
    const keys = Object.keys(controls);

    if (keys.length === 0) {
        container.innerHTML =
            '<p class="text-gray-500 italic p-6">No notification triggers configured.</p>';
        return;
    }

    const groups = getAdminNotificationSettingsGroups(controls);

    let html = '<div class="admin-notification-settings max-w-6xl space-y-8">';
    groups.forEach((group) => {
        const groupKeys = group.events.filter((event) => controls[event.id]);
        if (groupKeys.length === 0) {return;}
        const groupLabelAttr = escapeAttribute(group.label);
        const groupOrder = Number.isFinite(Number(group.order)) ?
            Number(group.order) :
            0;
        const groupIdAttr = escapeAttribute(group.id);

        html += `
            <div class="admin-notification-group bg-white rounded-lg shadow">
                <div class="border-b">
                    <div class="admin-notification-group-controls flex items-center justify-between gap-4 bg-white px-4 py-3">
                        <input
                            type="text"
                            class="notification-layout-input ${INLINE_EDIT_INPUT_CLASSES} admin-notification-group-heading min-w-0 flex-1"
                            value="${groupLabelAttr}"
                            data-group-id="${groupIdAttr}"
                            data-field="label"
                            aria-label="Notification group label"
                        >
                        <label class="admin-notification-order-field flex items-center gap-1.5 shrink-0">
                            <span>Order</span>
                            <input
                                type="number"
                                class="notification-layout-input admin-notification-order-input text-center"
                                value="${groupOrder}"
                                min="0"
                                max="999"
                                inputmode="numeric"
                                data-group-id="${groupIdAttr}"
                                data-field="order"
                                aria-label="Notification group order"
                            >
                        </label>
                    </div>
                    <div class="admin-notification-grid-header grid grid-cols-[1.5rem_minmax(14rem,2fr)_repeat(7,minmax(3.75rem,0.9fr))_2.5rem] gap-2 bg-gray-50 px-4 py-2 text-[10px] font-bold text-gray-500 uppercase text-center items-center">
                        <span></span>
                        <span class="text-left">Notification</span>
                        <span>Visible?</span>
                        <span>In-App Ctrl</span>
                        <span>In-App Def</span>
                        <span>Email Ctrl</span>
                        <span>Email Def</span>
                        <span>Time Ctrl</span>
                        <span>Time Def</span>
                        <span>Actions</span>
                    </div>
                </div>
                <div class="admin-notification-grid divide-y divide-gray-100">
                    ${groupKeys.map((event, index) => {
            const key = event.id;
            const escapedKey = escapeHTML(key);
            const keyAttr = escapeAttribute(key);
            const control = controls[key];
            const defaultConfig = defaults[key] || {
                inApp: false,
                email: false,
                days: 0,
            };
            const label = escapeHTML(event.label || getEventLabel(key));
            const hasTime = control.days !== undefined;
            const isFirstEvent = index === 0;
            const isLastEvent = index === groupKeys.length - 1;
            return `
                <div class="admin-notification-row px-4 py-2 grid grid-cols-[1.5rem_minmax(14rem,2fr)_repeat(7,minmax(3.75rem,0.9fr))_2.5rem] gap-2 items-center hover:bg-gray-50">
                    <div class="admin-notification-order-cell flex flex-col items-center">
                        <button
                            type="button"
                            class="notification-event-order-btn standard-action-icon-btn admin-notification-order-btn disabled:opacity-30 disabled:hover:text-gray-400"
                            data-event-id="${keyAttr}"
                            data-direction="-1"
                            title="Move up"
                            aria-label="Move ${escapeAttribute(event.label || getEventLabel(key))} up"
                            ${isFirstEvent ? "disabled" : ""}
                        >
                            <span class="material-symbols-outlined text-[16px] leading-none">keyboard_arrow_up</span>
                        </button>
                        <button
                            type="button"
                            class="notification-event-order-btn standard-action-icon-btn admin-notification-order-btn disabled:opacity-30 disabled:hover:text-gray-400"
                            data-event-id="${keyAttr}"
                            data-direction="1"
                            title="Move down"
                            aria-label="Move ${escapeAttribute(event.label || getEventLabel(key))} down"
                            ${isLastEvent ? "disabled" : ""}
                        >
                            <span class="material-symbols-outlined text-[16px] leading-none">keyboard_arrow_down</span>
                        </button>
                    </div>
                    <div class="admin-notification-name min-w-0 pr-2 leading-tight">
                        <div class="font-medium text-sm text-gray-900 leading-tight">${label}</div>
                        <div class="text-xs text-gray-400 font-mono leading-tight">${escapedKey}</div>
                    </div>
                    <div class="admin-notification-cell flex justify-center" data-mobile-label="Visible">
                        ${renderAdminNotificationToggle(control.visibility === "visible", `data-key="${keyAttr}" data-target="control" data-field="visibility" data-type="visibility"`)}
                    </div>
                    <div class="admin-notification-cell" data-mobile-label="In-App Ctrl">
                        <div
                            data-admin-notif-select-control
                            data-key="${keyAttr}"
                            data-target="control"
                            data-field="inApp"
                            data-value="${escapeAttribute(control.inApp || "user_control")}"
                            data-label="In-app control mode for ${escapeAttribute(event.label || getEventLabel(key))}"
                        ></div>
                    </div>
                    <div class="admin-notification-cell flex justify-center" data-mobile-label="In-App Default">
                        ${renderAdminNotificationToggle(defaultConfig.inApp, `data-key="${keyAttr}" data-target="default" data-field="inApp" data-type="bool"`)}
                    </div>
                    <div class="admin-notification-cell" data-mobile-label="Email Ctrl">
                        <div
                            data-admin-notif-select-control
                            data-key="${keyAttr}"
                            data-target="control"
                            data-field="email"
                            data-value="${escapeAttribute(control.email || "user_control")}"
                            data-label="Email control mode for ${escapeAttribute(event.label || getEventLabel(key))}"
                        ></div>
                    </div>
                    <div class="admin-notification-cell flex justify-center" data-mobile-label="Email Default">
                        ${renderAdminNotificationToggle(defaultConfig.email, `data-key="${keyAttr}" data-target="default" data-field="email" data-type="bool"`)}
                    </div>
                    <div class="admin-notification-cell flex justify-center" data-mobile-label="Time Ctrl">
                        ${hasTime ? `
                            <div
                                class="w-full"
                                data-admin-notif-select-control
                                data-key="${keyAttr}"
                                data-target="control"
                                data-field="days"
                                data-value="${escapeAttribute(control.days || "user_control")}"
                                data-label="Timing control mode for ${escapeAttribute(event.label || getEventLabel(key))}"
                            ></div>
                        ` : '<span class="text-gray-300">-</span>'}
                    </div>
                    <div class="admin-notification-cell flex justify-center items-center" data-mobile-label="Time Default">
                        ${hasTime ? `
                            <input type="number" class="admin-notif-input w-14 text-center" value="${defaultConfig.days || 0}" data-key="${keyAttr}" data-target="default" data-field="days">
                            <span class="text-[10px] text-gray-400 ml-1">d</span>
                        ` : '<span class="text-gray-300">-</span>'}
                    </div>
                    <div class="admin-notification-actions flex justify-center">
                        <button
                            type="button"
                            class="notification-event-move-btn standard-action-icon-btn"
                            data-event-id="${keyAttr}"
                            title="Change group"
                            aria-label="Change group for ${escapeAttribute(event.label || getEventLabel(key))}"
                        >
                            <span class="material-symbols-outlined text-[17px] leading-none">drive_file_move</span>
                        </button>
                    </div>
                </div>
            `;
        }).join("")}
                </div>
            </div>
        `;
    });

    html += `
        ${renderDrugNotificationTargetSettingsSection()}

        <div class="team-notification-group bg-white rounded-lg shadow overflow-x-auto mt-8">
            <div class="min-w-[44rem] bg-white px-4 py-3 border-b">
                <h4 class="font-semibold text-gray-700">Team Alerts</h4>
            </div>
            <div class="team-notification-grid-header min-w-[44rem] bg-gray-50 px-4 py-2 grid grid-cols-[minmax(10rem,1.1fr)_minmax(10rem,1fr)_minmax(12rem,1.15fr)_minmax(5rem,0.6fr)_minmax(7rem,0.75fr)] gap-3 text-[10px] font-bold text-gray-500 uppercase text-center items-center">
                <span class="text-left">Notification</span>
                <span class="text-left">Assigned Team</span>
                <span class="text-left">Urgency Filter</span>
                <span>Timing</span>
                <span>Alerts</span>
            </div>
            <div id="team-triggers-table-body" class="team-notification-grid min-w-[44rem]">
                <div class="p-6 text-center text-gray-400 text-sm">Loading team settings...</div>
            </div>
        </div>

    `;

    html += "</div>";
    destroyRegisteredControlsIn(container);
    container.innerHTML = html;

    mountAdminNotificationSelectControls(container);
    bindNativeFieldBehavior(container);

    fetchSharedTeams()
        .then(() => {
            renderTeamTriggers(getEventLabel);
        })
        .catch((err) => console.error("Failed to load teams:", err));

    container.querySelectorAll(".admin-notif-input, .admin-notif-toggle")
        .forEach((element) => {
            element.addEventListener("change", handleAdminNotificationChange);
        });

    container.querySelectorAll(".notification-layout-input")
        .forEach((element) => {
            element.addEventListener("change", (event) =>
                handleNotificationLayoutChange(event, container)
            );
        });

    container.querySelectorAll(".notification-event-order-btn")
        .forEach((button) => {
            button.addEventListener("click", (event) => {
                const target = event.currentTarget;
                const eventId = target.dataset.eventId;
                const direction = parseInt(target.dataset.direction, 10);
                moveNotificationEventWithinGroup(
                    eventId,
                    direction,
                    controls,
                    container,
                );
            });
        });

    const eventMap = new Map(groups.flatMap((group) =>
        group.events.map((event) => [event.id, event])
    ));
    container.querySelectorAll(".notification-event-move-btn")
        .forEach((button) => {
            button.addEventListener("click", (event) => {
                const eventId = event.currentTarget.dataset.eventId;
                const notificationEvent = eventMap.get(eventId);
                if (!notificationEvent) {return;}
                openNotificationGroupMoveModal(
                    notificationEvent,
                    controls,
                    container,
                );
            });
        });

    bindDrugNotificationTargetSettings(container, updateSaveButtonHandler);
}
